import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { usePermissions } from "@/hooks/usePermissions";
import { usePolicyTypes } from "@/lib/usePolicyTypes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Printer, ChevronRight, FolderTree, Copy } from "lucide-react";
import { toast } from "sonner";

// Mappa colore → classi badge (allineata a Policies.jsx COLOR_MAP)
const COLOR_DOT = {
  blue: "bg-blue-500", emerald: "bg-emerald-500", rose: "bg-rose-500",
  teal: "bg-teal-500", orange: "bg-orange-500", sky: "bg-sky-500",
  indigo: "bg-indigo-500", violet: "bg-violet-500", amber: "bg-amber-500",
  slate: "bg-slate-500", red: "bg-red-500", fuchsia: "bg-fuchsia-500", lime: "bg-lime-500",
};
const COLOR_BADGE = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  slate: "bg-slate-50 text-slate-700 border-slate-200",
  red: "bg-red-50 text-red-700 border-red-200",
  fuchsia: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
  lime: "bg-lime-50 text-lime-700 border-lime-200",
};

const emptyForm = { name: "", description: "", parent_id: "", suggested_type: "" };

const flattenTree = (cats, expanded) => {
  const map = new Map(cats.map((c) => [c.id, { ...c, children: [] }]));
  const roots = [];
  map.forEach((c) => {
    if (c.parent_id && map.has(c.parent_id)) map.get(c.parent_id).children.push(c);
    else roots.push(c);
  });
  const result = [];
  const walk = (node, depth) => {
    result.push({ ...node, depth, hasChildren: node.children.length > 0 });
    if (expanded.has(node.id)) {
      node.children.forEach((child) => walk(child, depth + 1));
    }
  };
  roots.forEach((r) => walk(r, 0));
  return result;
};

const CategoryRow = ({ cat, typeMeta, onEdit, onDelete, onDuplicate, onToggle, isExpanded, canEdit }) => {
  const depth = cat.depth || 0;
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`cat-row-${cat.id}`}>
      <td className="px-4 py-3" style={{ paddingLeft: `${16 + depth * 20}px` }}>
        <div className="flex items-center gap-2">
          {cat.hasChildren ? (
            <button
              data-testid={`toggle-cat-${cat.id}`}
              onClick={() => onToggle(cat.id)}
              className="text-slate-500 hover:text-slate-800 transition-transform"
              style={{ transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)" }}
              aria-label={isExpanded ? "Comprimi" : "Espandi"}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <span className="w-4" />
          )}
          <FolderTree className={`h-4 w-4 ${depth === 0 ? "text-blue-600" : "text-slate-400"}`} />
          <span className="font-medium">{cat.name}</span>
          {cat.hasChildren && <span className="text-[10px] text-slate-400 ml-1">({cat.children?.length || 0})</span>}
          {(cat.is_system || !cat.agency_id) ? (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-slate-300 bg-slate-100 text-slate-600" title="Categoria di sistema condivisa (non modificabile)">🔒 Sistema</span>
          ) : (
            <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700" title="Custom della tua agenzia">✏️ Custom</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-xs text-slate-500">{cat.description || "—"}</td>
      <td className="px-4 py-3 text-xs">
        {cat.suggested_type ? (
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide rounded-full border px-2 py-0.5 ${COLOR_BADGE[typeMeta?.color] || COLOR_BADGE.slate}`}>
            <span className={`h-2 w-2 rounded-full ${COLOR_DOT[typeMeta?.color] || COLOR_DOT.slate}`}></span>
            {typeMeta?.l || cat.suggested_type.replace(/_/g, " ")}
          </span>
        ) : "—"}
      </td>
      <td className="px-4 py-3 text-xs tabular-nums text-slate-600">{cat.policy_count || 0}</td>
      <td className="px-4 py-3 text-right space-x-2 print:hidden">
        {canEdit && (
          <>
            {(cat.is_system || !cat.agency_id) ? (
              <span className="text-slate-300 cursor-not-allowed" title="Categoria di sistema — non modificabile. Duplica o creane una custom.">
                <Pencil className="h-3.5 w-3.5 inline" />
              </span>
            ) : (
              <button data-testid={`edit-cat-${cat.id}`} onClick={() => onEdit(cat)} className="text-blue-600 hover:text-blue-800" title="Modifica"><Pencil className="h-3.5 w-3.5 inline" /></button>
            )}
            <button data-testid={`duplicate-cat-${cat.id}`} onClick={() => onDuplicate(cat)} className="text-emerald-600 hover:text-emerald-800" title="Duplica categoria"><Copy className="h-3.5 w-3.5 inline" /></button>
            {!(cat.is_system || !cat.agency_id) && (
              <button data-testid={`delete-cat-${cat.id}`} onClick={() => onDelete(cat)} className="text-red-600 hover:text-red-800" title="Elimina"><Trash2 className="h-3.5 w-3.5 inline" /></button>
            )}
          </>
        )}
      </td>
    </tr>
  );
};

export default function PolicyCategories() {
  const { canWrite } = usePermissions();
  const { options: dynPolicyTypes } = usePolicyTypes();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [expanded, setExpanded] = useState(new Set());
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/policy-categories"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = (parent_id = "") => {
    setEditing(null);
    setForm({ ...emptyForm, parent_id });
    setOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    setForm({
      name: cat.name,
      description: cat.description || "",
      parent_id: cat.parent_id || "",
      suggested_type: cat.suggested_type || "",
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        parent_id: form.parent_id || null,
        suggested_type: form.suggested_type || null,
      };
      if (editing) await api.put(`/policy-categories/${editing.id}`, payload);
      else await api.post("/policy-categories", payload);
      toast.success(editing ? "Categoria aggiornata" : "Categoria creata");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = (cat) => {
    setDeleteTarget({ id: cat.id, name: cat.name });
  };

  // 🆕 09/07/2026 — Duplica una categoria esistente (copia nome+descrizione+tipologia+padre,
  // aggiunge il suffisso "(copia)" al nome; l'utente può poi rinominare/salvare).
  const duplicate = async (cat) => {
    try {
      const baseName = (cat.name || "").trim();
      // Trova un nome unico "(copia)", "(copia 2)", ecc.
      const existingNames = new Set(items.map((c) => (c.name || "").toLowerCase()));
      let newName = `${baseName} (copia)`;
      let n = 2;
      while (existingNames.has(newName.toLowerCase())) {
        newName = `${baseName} (copia ${n})`;
        n += 1;
      }
      const payload = {
        name: newName,
        description: cat.description || null,
        parent_id: cat.parent_id || null,
        suggested_type: cat.suggested_type || null,
      };
      const { data } = await api.post("/policy-categories", payload);
      toast.success(`Categoria duplicata come "${newName}"`);
      await load();
      // Apri direttamente in modifica per consentire aggiustamenti immediati
      if (data?.id) {
        openEdit({
          id: data.id,
          name: newName,
          description: payload.description,
          parent_id: payload.parent_id,
          suggested_type: payload.suggested_type,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/policy-categories/${deleteTarget.id}`);
      toast.success("Eliminata");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const flat = useMemo(() => flattenTree(items, expanded), [items, expanded]);
  const hasChildren = useMemo(() => new Set(items.filter((c) => c.parent_id).map((c) => c.parent_id)), [items]);
  const allExpanded = items.length > 0 && [...hasChildren].every((id) => expanded.has(id));
  const toggle = (id) => setExpanded((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setExpanded(allExpanded ? new Set() : new Set(hasChildren));
  const parentOptions = useMemo(
    () => items.filter((c) => !editing || c.id !== editing.id),
    [items, editing],
  );
  const typeOptions = useMemo(() => (dynPolicyTypes || []).filter((t) => t.v), [dynPolicyTypes]);
  const findType = (code) => typeOptions.find((t) => t.v === code) || null;

  return (
    <div className="space-y-6 animate-fade-up" data-testid="categories-page">
      <header className="flex items-center justify-between flex-wrap gap-4 print:hidden">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 mb-2">Configurazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Categorie Polizze</h1>
          <p className="text-slate-500 mt-1">{items.length} categorie · struttura gerarchica + tipologia base</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="print-cats" onClick={() => window.print()}><Printer className="h-4 w-4 mr-2" /> Stampa</Button>
          {hasChildren.size > 0 && (
            <Button variant="outline" data-testid="toggle-all-cats" onClick={toggleAll}>
              {allExpanded ? "− Comprimi tutto" : "+ Espandi tutto"}
            </Button>
          )}
          {canWrite("categories") && (
            <Button data-testid="add-cat-btn" onClick={() => openCreate()} className="bg-[#0F172A] hover:bg-[#1E293B]"><Plus className="h-4 w-4 mr-2" /> Nuova categoria</Button>
          )}
        </div>
      </header>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-violet-600 text-white text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3">Nome</th>
              <th className="text-left px-4 py-3">Descrizione</th>
              <th className="text-left px-4 py-3">Tipologia base</th>
              <th className="text-left px-4 py-3">Polizze</th>
              <th className="text-right px-4 py-3 print:hidden">Azioni</th>
            </tr>
          </thead>
          <tbody data-testid="cats-table">
            {flat.length === 0 ? (
              <tr><td colSpan="5" className="px-4 py-12 text-center text-slate-400 text-sm">Nessuna categoria. Crea la prima.</td></tr>
            ) : (
              flat.map((c) => <CategoryRow key={c.id} cat={c} typeMeta={findType(c.suggested_type)} onEdit={openEdit} onDelete={remove} onDuplicate={duplicate} onToggle={toggle} isExpanded={expanded.has(c.id)} canEdit={canWrite("categories")} />)
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Modifica categoria" : "Nuova categoria"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input data-testid="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Es. Auto storiche" /></div>
            <div><Label>Descrizione (facoltativa)</Label><Input data-testid="cat-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            <div>
              <Label>Categoria padre (facoltativa)</Label>
              <Select value={form.parent_id || "__none__"} onValueChange={(v) => setForm({ ...form, parent_id: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="cat-parent"><SelectValue placeholder="Nessuna (root)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuna (root)</SelectItem>
                  {parentOptions.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipologia base associata</Label>
              <Select value={form.suggested_type || "__none__"} onValueChange={(v) => setForm({ ...form, suggested_type: v === "__none__" ? "" : v })}>
                <SelectTrigger data-testid="cat-type">
                  {(() => {
                    const sel = findType(form.suggested_type);
                    if (!sel) return <SelectValue placeholder="— Nessuna —" />;
                    return (
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${COLOR_BADGE[sel.color] || COLOR_BADGE.slate}`}>
                        <span className={`h-2 w-2 rounded-full ${COLOR_DOT[sel.color] || COLOR_DOT.slate}`}></span>
                        {sel.l}
                      </span>
                    );
                  })()}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— Nessuna —</SelectItem>
                  {typeOptions.map((t) => (
                    <SelectItem key={t.v} value={t.v}>
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${COLOR_DOT[t.color] || COLOR_DOT.slate}`}></span>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded border ${COLOR_BADGE[t.color] || COLOR_BADGE.slate}`}>{t.l}</span>
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1">Quando crei una polizza in questa categoria, il campo Tipo verrà precompilato.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button data-testid="save-cat-btn" disabled={!form.name.trim()} onClick={submit} className="bg-[#0F172A] hover:bg-[#1E293B]">{editing ? "Salva" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione categoria */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o && !deleting) setDeleteTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="confirm-delete-category-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="h-5 w-5" /> Eliminare la categoria?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-700 space-y-2">
            <p>Stai per eliminare la categoria:</p>
            <p className="font-semibold bg-slate-50 border border-slate-200 rounded px-3 py-2">
              {deleteTarget?.name}
            </p>
            <p className="text-xs text-red-600">L&apos;operazione non può essere annullata. Le polizze già collegate manterranno il riferimento ma la categoria non sarà più selezionabile.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="cancel-delete-category" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Annulla
            </Button>
            <Button
              data-testid="confirm-delete-category"
              onClick={confirmDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting ? "Eliminazione…" : "Elimina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
